import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const EntityFilterContext = createContext(null);

export const EntityFilterProvider = ({ children }) => {
  const [entities, setEntities] = useState([]);
  const [entityOptions, setEntityOptions] = useState([]);
  const [selectedEntity, setSelectedEntity] = useState('all');
  const [groupStructure, setGroupStructure] = useState(null);
  const [hasCosecModule, setHasCosecModule] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const token = localStorage.getItem('session_token');

  const fetchEntityStructure = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await axios.get(`${API}/api/entity-filter/structure`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setEntities(response.data.entities || []);
      setGroupStructure(response.data.group_structure);
      setHasCosecModule(response.data.has_cosec_module);
      
      // Build options for dropdown
      const options = [
        { value: 'all', label: 'All Entities', type: 'filter' },
      ];
      
      if (response.data.entities?.length > 1) {
        options.push({ value: 'group', label: 'Group/Holding Co Only', type: 'filter' });
      }
      
      // Add individual entities with hierarchy indication
      (response.data.entities || []).forEach(entity => {
        const prefix = entity.parent_entity_id ? '└─ ' : '';
        options.push({
          value: entity.entity_id,
          label: `${prefix}${entity.name}`,
          type: entity.entity_type || 'company',
          jurisdiction: entity.jurisdiction_country,
          isSubsidiary: !!entity.parent_entity_id
        });
      });
      
      setEntityOptions(options);
      setError(null);
    } catch (err) {
      // Don't show error for auth issues
      if (err.response?.status !== 401 && err.response?.status !== 403) {
        setError('Failed to load entities');
        console.error('Entity fetch error:', err);
      }
      // Set default entity options even on error
      setEntityOptions([{ value: 'all', label: 'All Entities', type: 'filter' }]);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchEntityStructure();
  }, [fetchEntityStructure]);

  const getEntityById = useCallback((entityId) => {
    return entities.find(e => e.entity_id === entityId);
  }, [entities]);

  const getEntityName = useCallback((entityId) => {
    if (entityId === 'all') return 'All Entities';
    if (entityId === 'group') return 'Group/Holding Co';
    const entity = getEntityById(entityId);
    return entity?.name || entityId;
  }, [getEntityById]);

  const getFilteredEntityIds = useCallback(() => {
    if (selectedEntity === 'all') {
      return entities.map(e => e.entity_id);
    }
    if (selectedEntity === 'group') {
      return entities.filter(e => !e.parent_entity_id).map(e => e.entity_id);
    }
    return [selectedEntity];
  }, [selectedEntity, entities]);

  const value = {
    // State
    entities,
    entityOptions,
    selectedEntity,
    groupStructure,
    hasCosecModule,
    loading,
    error,
    
    // Actions
    setSelectedEntity,
    refreshEntities: fetchEntityStructure,
    
    // Helpers
    getEntityById,
    getEntityName,
    getFilteredEntityIds,
    
    // Computed
    hasMultipleEntities: entities.length > 1,
    showEntityFilter: entities.length > 1 || hasCosecModule
  };

  return (
    <EntityFilterContext.Provider value={value}>
      {children}
    </EntityFilterContext.Provider>
  );
};

export const useEntityFilter = () => {
  const context = useContext(EntityFilterContext);
  if (!context) {
    throw new Error('useEntityFilter must be used within an EntityFilterProvider');
  }
  return context;
};

export default EntityFilterContext;
